import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Search, Building2, Mail, Phone, DollarSign, User, Clock, Trash2, X,
  Target, TrendingUp, ChevronRight, Send, Inbox, ExternalLink, Link2, RefreshCw,
  LayoutGrid, LayoutList, Download, Upload, Filter, ArrowUpDown, Globe, MapPin, Users,
  Briefcase, Pencil, ChevronDown, ScanLine, QrCode, FileText, UserPlus
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { Acronym } from '../components/shared/AcronymTooltip';
import { BulkUploadDialog } from '../components/BulkUploadDialog';
import { useCurrency } from '../contexts/CurrencyContext';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { INDUSTRIES as INDUSTRY_OPTIONS, JOB_TITLES, COUNTRIES, REVENUE_RANGES, EMPLOYEE_RANGES } from '../data/dropdownOptions';

const API = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { id: 'new', label: 'New', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  { id: 'contacted', label: 'Contacted', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  { id: 'qualified', label: 'Qualified', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  { id: 'proposal', label: 'Proposal', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-pink-100 text-pink-700', dot: 'bg-pink-500' },
  { id: 'won', label: 'Won', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  { id: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
];
const SOURCES = [
  { id: 'manual', label: 'Manual', icon: FileText },
  { id: 'form', label: 'Form', icon: Globe },
  { id: 'card_scan', label: 'Card Scan', icon: ScanLine },
  { id: 'qr_scan', label: 'QR Scan', icon: QrCode },
  { id: 'referral', label: 'Referral', icon: Users },
  { id: 'website', label: 'Website', icon: Globe },
  { id: 'social', label: 'Social', icon: Globe },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'phone', label: 'Phone', icon: Phone },
  { id: 'event', label: 'Event', icon: Users },
];
const SORT_OPTIONS = [
  { value: 'created_at', label: 'Date Added' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'company', label: 'Company' },
  { value: 'area', label: 'Area' },
  { value: 'industry', label: 'Industry' },
  { value: 'deal_value', label: 'Deal Value' },
  { value: 'score', label: 'Lead Score' },
];

// ─── Contact Modal (Add/Edit) ───
function ContactModal({ open, onClose, onSaved, editing }) {
  const { symbol: currSymbol } = useCurrency();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic');
  const [suggestions, setSuggestions] = useState({});
  useEffect(() => {
    if (editing) setForm({ ...editing });
    else setForm({ first_name: '', last_name: '', email: '', phone: '', company: '', job_title: '', source: 'manual', stage: 'new', deal_value: 0, notes: '', tags: '', industry: '', area: '', city: '', country: '', website: '', linkedin: '', instagram: '', facebook: '', twitter: '', tiktok: '', youtube: '', num_employees: '', annual_revenue: '', address: '', birthday: '', opted_in: true });
  }, [editing, open]);
  useEffect(() => {
    if (!open) return;
    const fetchSuggestions = async () => {
      try {
        const fields = ['job_title', 'industry', 'country'];
        const results = await Promise.all(fields.map(f => axios.get(`${API}/api/leads/field-suggestions?field=${f}`).catch(() => ({ data: { suggestions: [] } }))));
        setSuggestions({ job_title: results[0].data.suggestions, industry: results[1].data.suggestions, country: results[2].data.suggestions });
      } catch { /* */ }
    };
    fetchSuggestions();
  }, [open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name?.trim()) { toast.error('First name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags || [], deal_value: parseFloat(form.deal_value) || 0 };
      const { data } = editing
        ? await axios.put(`${API}/api/leads/${editing.id}`, payload)
        : await axios.post(`${API}/api/leads`, payload);
      toast.success(editing ? 'Updated' : 'Contact added');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  const F = ({ label, field, ph, type = 'text', span = 1 }) => (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <Label className="text-[10px]">{label}</Label>
      <Input type={type} value={form[field] || ''} onChange={e => setForm(p => ({...p, [field]: e.target.value}))} placeholder={ph} className="h-8 text-sm" data-testid={`contact-${field}`} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><User className="h-5 w-5 text-blue-500" />{editing ? 'Edit' : 'Add'} Contact</DialogTitle><DialogDescription>All fields are saved to the CRM database</DialogDescription></DialogHeader>
        <div className="flex gap-1 mb-3 border-b">
          {['basic','company','social','extra'].map(t => <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${tab === t ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>{t === 'basic' ? 'Contact' : t === 'company' ? 'Company' : t === 'social' ? 'Social' : 'Details'}</button>)}
        </div>
        <form onSubmit={handleSubmit} className="space-y-2">
          {tab === 'basic' && <div className="grid grid-cols-2 gap-2">
            <F label="First Name *" field="first_name" ph="John" />
            <F label="Last Name" field="last_name" ph="Doe" />
            <F label="Email" field="email" ph="john@co.com" />
            <F label="Phone" field="phone" ph="+1234567890" />
            <div><Label className="text-[10px]">Source</Label>
              <Select value={form.source || 'manual'} onValueChange={v => setForm(p => ({...p, source: v}))}><SelectTrigger className="h-8 text-sm" data-testid="contact-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-[10px]">Stage</Label>
              <Select value={form.stage || 'new'} onValueChange={v => setForm(p => ({...p, stage: v}))}><SelectTrigger className="h-8 text-sm" data-testid="contact-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent></Select></div>
            <F label="Tags (comma separated)" field="tags" ph="vip, prospect" span={2} />
          </div>}
          {tab === 'company' && <div className="grid grid-cols-2 gap-2">
            <F label="Company" field="company" ph="Acme Inc." />
            <div><Label className="text-[10px]">Job Title</Label>
              <SearchableSelect value={form.job_title || ''} onValueChange={v => setForm(p => ({...p, job_title: v}))} options={JOB_TITLES} placeholder="Select title..." searchPlaceholder="Search titles..." triggerClassName="h-8 text-sm" frequentValues={suggestions.job_title} data-testid="contact-job-title" /></div>
            <div><Label className="text-[10px]">Industry</Label>
              <SearchableSelect value={form.industry || ''} onValueChange={v => setForm(p => ({...p, industry: v}))} options={INDUSTRY_OPTIONS} placeholder="Select industry..." searchPlaceholder="Search industries..." triggerClassName="h-8 text-sm" frequentValues={suggestions.industry} data-testid="contact-industry" /></div>
            <div><Label className="text-[10px]">Employees</Label>
              <Select value={form.num_employees || ''} onValueChange={v => setForm(p => ({...p, num_employees: v}))}><SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{EMPLOYEE_RANGES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-[10px]">Annual Revenue</Label>
              <Select value={form.annual_revenue || ''} onValueChange={v => setForm(p => ({...p, annual_revenue: v}))}><SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select range..." /></SelectTrigger>
                <SelectContent>{REVENUE_RANGES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
          </div>}
          {tab === 'social' && <div className="grid grid-cols-2 gap-2">
            <F label="Website" field="website" ph="https://acme.com" />
            <F label="LinkedIn" field="linkedin" ph="linkedin.com/in/john" />
            <F label="Instagram" field="instagram" ph="@johndoe" />
            <F label="Facebook" field="facebook" ph="facebook.com/johndoe" />
            <F label="Twitter / X" field="twitter" ph="@johndoe" />
            <F label="TikTok" field="tiktok" ph="@johndoe" />
            <F label="YouTube" field="youtube" ph="youtube.com/@johndoe" />
            <F label="Birthday" field="birthday" ph="" type="date" />
          </div>}
          {tab === 'extra' && <div className="grid grid-cols-2 gap-2">
            <F label="City" field="city" ph="New York" />
            <div><Label className="text-[10px]">Country</Label>
              <SearchableSelect value={form.country || ''} onValueChange={v => setForm(p => ({...p, country: v}))} options={COUNTRIES} placeholder="Select country..." searchPlaceholder="Search countries..." triggerClassName="h-8 text-sm" frequentValues={suggestions.country} data-testid="contact-country" /></div>
            <F label="Area / Region" field="area" ph="North America" />
            <F label={`Deal Value (${currSymbol})`} field="deal_value" ph="0" type="number" />
            <F label="Address" field="address" ph="123 Main St" span={2} />
            <div className="col-span-2"><Label className="text-[10px]">Notes</Label><Textarea value={form.notes || ''} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} className="text-sm" data-testid="contact-notes" /></div>
            <div className="flex items-center gap-2 col-span-2"><Switch checked={form.opted_in !== false} onCheckedChange={v => setForm(p => ({...p, opted_in: v}))} /><Label className="text-xs">Opted in for communications</Label></div>
          </div>}
          <DialogFooter className="pt-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-contact-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Add Contact'}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Contact Detail Drawer ───
function ContactDetail({ lead, onClose, onUpdated, onDeleted }) {
  const { format: fc } = useCurrency();
  const [activities, setActivities] = useState([]);
  const [actNote, setActNote] = useState('');
  useEffect(() => {
    if (lead?.id) axios.get(`${API}/api/leads/${lead.id}/activities`).then(r => setActivities(r.data)).catch(() => {});
  }, [lead?.id]);
  const addActivity = async () => {
    if (!actNote.trim()) return;
    try {
      const { data } = await axios.post(`${API}/api/leads/${lead.id}/activities`, { type: 'note', description: actNote });
      setActivities(p => [data, ...p]); setActNote('');
    } catch { toast.error('Failed'); }
  };
  const stage = STAGES.find(s => s.id === lead?.stage) || STAGES[0];
  const source = SOURCES.find(s => s.id === lead?.source);
  if (!lead) return null;
  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white shadow-2xl border-l z-50 flex flex-col animate-slideIn" data-testid="contact-detail-drawer">
      <div className="p-4 border-b flex items-center justify-between bg-gray-50">
        <div>
          <h2 className="text-base font-bold text-gray-900">{lead.first_name} {lead.last_name}</h2>
          <p className="text-xs text-gray-400">{lead.job_title}{lead.job_title && lead.company ? ' at ' : ''}{lead.company}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-detail"><X className="h-5 w-5" /></Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Badge className={stage.color + ' text-[9px]'}>{stage.label}</Badge>
            {source && <Badge variant="outline" className="text-[9px]">{source.label}</Badge>}
            {lead.score_label && <Badge className={`text-[9px] ${lead.score_label === 'A' ? 'bg-green-100 text-green-700' : lead.score_label === 'B' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>Score: {lead.score_label}</Badge>}
          </div>
          {lead.email && <p className="text-xs flex items-center gap-2"><Mail className="h-3 w-3 text-gray-400" /><a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">{lead.email}</a></p>}
          {lead.phone && <p className="text-xs flex items-center gap-2"><Phone className="h-3 w-3 text-gray-400" /><a href={`tel:${lead.phone}`} className="text-blue-600 hover:underline">{lead.phone}</a></p>}
          {lead.company && <p className="text-xs flex items-center gap-2"><Building2 className="h-3 w-3 text-gray-400" />{lead.company}{lead.industry ? ` · ${lead.industry.replace(/_/g,' ')}` : ''}</p>}
          {lead.num_employees && <p className="text-xs flex items-center gap-2"><Users className="h-3 w-3 text-gray-400" />{lead.num_employees} employees</p>}
          {(lead.city || lead.country) && <p className="text-xs flex items-center gap-2"><MapPin className="h-3 w-3 text-gray-400" />{[lead.city, lead.area, lead.country].filter(Boolean).join(', ')}</p>}
          {lead.website && <p className="text-xs flex items-center gap-2"><Globe className="h-3 w-3 text-gray-400" /><a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{lead.website}</a></p>}
          {lead.linkedin && <p className="text-xs flex items-center gap-2"><span className="text-[10px] text-gray-400">in</span><a href={lead.linkedin.startsWith('http') ? lead.linkedin : `https://${lead.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate text-[10px]">{lead.linkedin}</a></p>}
          {lead.instagram && <p className="text-xs flex items-center gap-2"><span className="text-[10px] text-gray-400">IG</span><span className="text-[10px] text-gray-600">{lead.instagram}</span></p>}
          {lead.facebook && <p className="text-xs flex items-center gap-2"><span className="text-[10px] text-gray-400">FB</span><span className="text-[10px] text-gray-600">{lead.facebook}</span></p>}
          {lead.twitter && <p className="text-xs flex items-center gap-2"><span className="text-[10px] text-gray-400">X</span><span className="text-[10px] text-gray-600">{lead.twitter}</span></p>}
          {lead.birthday && <p className="text-xs flex items-center gap-2"><span className="text-[10px] text-gray-400">BD</span><span className="text-[10px] text-gray-600">{lead.birthday}</span></p>}
          {lead.deal_value > 0 && <p className="text-xs flex items-center gap-2"><DollarSign className="h-3 w-3 text-gray-400" />{fc(lead.deal_value)}</p>}
          {lead.created_by && lead.created_by !== 'form_submission' && <p className="text-[10px] text-gray-400 flex items-center gap-1 mt-1"><User className="h-2.5 w-2.5" />Contributed by: {lead.created_by_name || lead.created_by}{lead.created_at && ` — ${new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}</p>}
          {lead.created_by === 'form_submission' && <p className="text-[10px] text-gray-400 flex items-center gap-1 mt-1"><Globe className="h-2.5 w-2.5" />Submitted via public form{lead.created_at && ` — ${new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}</p>}
          {lead.notes && <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded">{lead.notes}</p>}
        </div>
        <div className="px-4 py-3 border-t">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Activity</p>
          <div className="flex gap-2 mb-3">
            <Input value={actNote} onChange={e => setActNote(e.target.value)} placeholder="Add a note..." className="text-xs h-8" onKeyDown={e => e.key === 'Enter' && addActivity()} data-testid="activity-note-input" />
            <Button size="sm" className="h-8 bg-blue-600" onClick={addActivity}>Add</Button>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {activities.map(a => (
              <div key={a.id} className="flex gap-2 p-2 bg-gray-50 rounded text-[10px]">
                <div className={`w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${a.type === 'stage_change' ? 'bg-purple-500' : a.type === 'email' ? 'bg-blue-500' : 'bg-gray-400'}`} />
                <div className="flex-1"><p className="text-gray-700">{a.description}</p><p className="text-gray-400">{a.created_by_name}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="p-3 border-t flex gap-2">
        <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => onUpdated(lead)} data-testid="edit-contact-btn"><Pencil className="h-3 w-3" />Edit</Button>
        <Button size="sm" className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={async () => {
          try {
            const res = await axios.post(`${API}/api/clients/convert/${lead.id}`);
            if (res.data.already_client) toast.info('Already a client');
            else toast.success('Converted to client!');
          } catch (e) { toast.error(e.response?.data?.detail || 'Failed to convert'); }
        }} data-testid="convert-to-client-btn"><UserPlus className="h-3 w-3" />Convert to Client</Button>
        <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600 gap-1" onClick={() => { if (window.confirm('Delete contact?')) onDeleted(lead.id); }} data-testid="delete-contact-btn"><Trash2 className="h-3 w-3" />Delete</Button>
      </div>
    </div>
  );
}

// ─── Main Page ───
export default function LeadPipelinePage() {
  const { format: fc } = useCurrency();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState(-1);
  const [viewMode, setViewMode] = useState(() => window.innerWidth < 640 ? 'card' : 'list');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const searchTimer = useRef(null);

  const fetchLeads = useCallback(async () => {
    try {
      const params = { sort_by: sortBy, sort_order: sortOrder };
      if (filterStage !== 'all') params.stage = filterStage;
      if (filterSource !== 'all') params.source = filterSource;
      if (filterIndustry !== 'all') params.industry = filterIndustry;
      if (search) params.search = search;
      const [l, s] = await Promise.all([
        axios.get(`${API}/api/leads`, { params,  }),
        axios.get(`${API}/api/leads/stats`),
      ]);
      setLeads(l.data); setStats(s.data);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  }, [filterStage, filterSource, filterIndustry, sortBy, sortOrder, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const onSearch = (val) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(val), 300);
  };

  const onSaved = (c, isUpdate) => { if (isUpdate) setLeads(p => p.map(x => x.id === c.id ? c : x)); else setLeads(p => [c, ...p]); if (isUpdate && selectedLead?.id === c.id) setSelectedLead(c); };
  const onDelete = async (id) => { try { await axios.delete(`${API}/api/leads/${id}`); setLeads(p => p.filter(l => l.id !== id)); setSelectedLead(null); toast.success('Deleted'); } catch { toast.error('Failed'); } };
  const exportCSV = async () => { try { const res = await axios.get(`${API}/api/leads/export/csv`, { responseType: 'blob' }); const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = 'konnect_contacts.csv'; a.click(); URL.revokeObjectURL(url); toast.success('Exported!'); } catch { toast.error('Export failed'); } };
  const toggleSort = (field) => { if (sortBy === field) setSortOrder(p => p * -1); else { setSortBy(field); setSortOrder(1); } };

  const getStage = (id) => STAGES.find(s => s.id === id) || STAGES[0];
  const getSource = (id) => SOURCES.find(s => s.id === id) || SOURCES[0];

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="contacts-crm-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Users className="h-5 w-5 text-blue-500" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Contacts <Acronym term="CRM" /><PageInfoTooltip pageKey="contacts-crm" /></h1>
            <p className="text-xs text-gray-400">{leads.length} contacts · {stats?.total_value ? `${fc(stats.total_value)} pipeline` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1 h-8" onClick={exportCSV} data-testid="export-csv-btn"><Download className="h-3 w-3" />Export</Button>
          <Button variant="outline" size="sm" className="gap-1 h-8" onClick={() => setBulkOpen(true)} data-testid="bulk-import-btn"><Upload className="h-3 w-3" />Import</Button>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 h-8 gap-1" data-testid="add-contact-btn"><Plus className="h-4 w-4" />Add Contact</Button>
        </div>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {STAGES.map(s => {
            const st = stats.stages?.[s.id] || { count: 0 };
            return <Badge key={s.id} className={`${s.color} text-[9px] whitespace-nowrap cursor-pointer ${filterStage === s.id ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`} onClick={() => setFilterStage(filterStage === s.id ? 'all' : s.id)} data-testid={`stage-filter-${s.id}`}>{s.label}: {st.count}</Badge>;
          })}
        </div>
      )}

      {/* Search + Filters + View Toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input placeholder="Search contacts..." onChange={e => onSearch(e.target.value)} className="pl-8 h-9 text-sm" data-testid="search-contacts" />
        </div>
        <Button variant="outline" size="sm" className={`gap-1 h-9 ${showFilters ? 'bg-blue-50 border-blue-200' : ''}`} onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters"><Filter className="h-3.5 w-3.5" />Filters</Button>
        <Select value={sortBy} onValueChange={v => { setSortBy(v); setSortOrder(v === 'created_at' ? -1 : 1); }}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="sort-select"><ArrowUpDown className="h-3 w-3 mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>{SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex border rounded-md">
          <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="icon" className={`h-9 w-9 rounded-r-none ${viewMode === 'list' ? 'bg-blue-600' : ''}`} onClick={() => setViewMode('list')} data-testid="view-list"><LayoutList className="h-4 w-4" /></Button>
          <Button variant={viewMode === 'card' ? 'default' : 'ghost'} size="icon" className={`h-9 w-9 rounded-l-none ${viewMode === 'card' ? 'bg-blue-600' : ''}`} onClick={() => setViewMode('card')} data-testid="view-card"><LayoutGrid className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="flex items-center gap-2 flex-wrap p-3 bg-gray-50 rounded-lg">
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-28 h-8 text-xs"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Sources</SelectItem>{SOURCES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterIndustry} onValueChange={setFilterIndustry}>
            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="Industry" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Industries</SelectItem>{INDUSTRY_OPTIONS.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="text-xs h-8" onClick={() => { setFilterStage('all'); setFilterSource('all'); setFilterIndustry('all'); setSearch(''); }}>Clear All</Button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" /></div>
      ) : leads.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No contacts yet</h3>
          <p className="text-sm text-gray-400 mb-4">Add contacts manually, scan business cards, or share your capture form</p>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 gap-1"><Plus className="h-4 w-4" />Add Contact</Button>
        </CardContent></Card>
      ) : viewMode === 'list' ? (
        /* ─── List View ─── */
        <div className="border rounded-xl overflow-hidden bg-white overflow-x-auto">
          <div className="grid grid-cols-[2fr_1.5fr_1fr_1fr_0.8fr_80px] gap-2 px-4 py-2 bg-gray-50 text-[10px] font-semibold text-gray-500 uppercase border-b min-w-[700px]">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => toggleSort('first_name')}>Name {sortBy === 'first_name' && (sortOrder === 1 ? '↑' : '↓')}</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => toggleSort('company')}>Company {sortBy === 'company' && (sortOrder === 1 ? '↑' : '↓')}</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => toggleSort('area')}>Location {sortBy === 'area' && (sortOrder === 1 ? '↑' : '↓')}</span>
            <span>Stage</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => toggleSort('score')}>Score {sortBy === 'score' && (sortOrder === 1 ? '↑' : '↓')}</span>
            <span>Contributed</span>
          </div>
          {leads.map(l => {
            const stage = getStage(l.stage);
            const src = getSource(l.source);
            return (
              <div key={l.id} className="grid grid-cols-[2fr_1.5fr_1fr_1fr_0.8fr_80px] gap-2 px-4 py-2.5 border-b hover:bg-blue-50/30 cursor-pointer transition-colors items-center min-w-[700px]" onClick={() => setSelectedLead(l)} data-testid={`contact-row-${l.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{l.first_name} {l.last_name}</p>
                  <p className="text-[10px] text-gray-400 truncate">{l.email || l.phone}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-700 truncate">{l.company || '-'}</p>
                  {l.industry && <p className="text-[10px] text-gray-400 truncate">{l.industry.replace(/_/g,' ')}</p>}
                </div>
                <p className="text-xs text-gray-600 truncate">{[l.city, l.country].filter(Boolean).join(', ') || '-'}</p>
                <Badge className={`${stage.color} text-[9px] w-fit`}>{stage.label}</Badge>
                <Badge className={`text-[9px] w-fit ${l.score_label === 'A' ? 'bg-green-100 text-green-700' : l.score_label === 'B' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{l.score_label || 'C'}</Badge>
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-600 truncate">{(l.created_by_name && l.created_by_name !== 'form_submission') ? l.created_by_name : (l.created_by === 'form_submission' || l.source === 'form_submission' ? 'Public Form' : l.source?.replace(/_/g,' ') || '-')}</p>
                  {l.created_at && <p className="text-[9px] text-gray-400 truncate">{new Date(l.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ─── Card View ─── */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {leads.map(l => {
            const stage = getStage(l.stage);
            return (
              <Card key={l.id} className="border-gray-200 hover:shadow-md cursor-pointer transition-all" onClick={() => setSelectedLead(l)} data-testid={`contact-card-${l.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-blue-600">{(l.first_name?.[0] || '').toUpperCase()}{(l.last_name?.[0] || '').toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 truncate">{l.first_name} {l.last_name}</h3>
                      <p className="text-[10px] text-gray-400 truncate">{l.job_title}{l.job_title && l.company ? ' at ' : ''}{l.company}</p>
                    </div>
                    <Badge className={`${stage.color} text-[9px]`}>{stage.label}</Badge>
                  </div>
                  <div className="space-y-1">
                    {l.email && <p className="text-[10px] text-gray-500 flex items-center gap-1.5 truncate"><Mail className="h-2.5 w-2.5 flex-shrink-0" />{l.email}</p>}
                    {l.phone && <p className="text-[10px] text-gray-500 flex items-center gap-1.5"><Phone className="h-2.5 w-2.5 flex-shrink-0" />{l.phone}</p>}
                    {(l.city || l.country) && <p className="text-[10px] text-gray-500 flex items-center gap-1.5"><MapPin className="h-2.5 w-2.5 flex-shrink-0" />{[l.city, l.country].filter(Boolean).join(', ')}</p>}
                    {l.industry && <p className="text-[10px] text-gray-500 flex items-center gap-1.5"><Briefcase className="h-2.5 w-2.5 flex-shrink-0" />{l.industry.replace(/_/g,' ')}{l.num_employees ? ` · ${l.num_employees} employees` : ''}</p>}
                  </div>
                  {l.deal_value > 0 && <p className="text-xs font-semibold text-green-600 mt-2">{fc(l.deal_value)}</p>}
                  {(l.created_by_name || l.created_by === 'form_submission') && <p className="text-[9px] text-gray-400 mt-2 flex items-center gap-1 border-t border-gray-100 pt-1.5"><User className="h-2.5 w-2.5" />{l.created_by_name && l.created_by_name !== 'form_submission' ? l.created_by_name : 'Public Form'}{l.created_at && <span className="ml-auto">{new Date(l.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Drawer */}
      {selectedLead && <ContactDetail lead={selectedLead} onClose={() => setSelectedLead(null)} onUpdated={(l) => { setEditing(l); setModalOpen(true); setSelectedLead(null); }} onDeleted={onDelete} />}

      {/* Modal */}
      <ContactModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={onSaved} editing={editing} />
      <BulkUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={fetchLeads}
        endpoint="/api/leads/import/csv" entityName="Leads"
        templateFields={["first_name","last_name","email","phone","company","job_title","industry","city","country","source","stage","deal_value","notes","tags"]} />
    </div>
  );
}
